/**
 * @fileoverview SortingHat Component - Handles the magical house assignment process
 * 
 * This component implements a balanced house assignment system that ensures fair distribution
 * of students across all four Hogwarts houses while maintaining the magical experience.
 * 
 * Features:
 * - Balanced algorithm: Maintains house populations within ±1 student
 * - Prevents duplicate sorting for already-assigned students
 * - Animated sorting experience with hat imagery
 * - Automatic persistence to both localStorage and backend database
 * - Error handling for network failures and edge cases
 * 
 * House Assignment Logic:
 * 1. Fetches current house populations from the school's student roster
 * 2. Identifies candidate houses that maintain balance (max-min ≤ 1 after assignment)
 * 3. Randomly selects from valid candidates, or falls back to least populated houses
 * 4. Updates user data locally and persists to server
 * 
 * @author Harry Potter Quiz App
 * @version 1.0.0
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../App.jsx';

/** @constant {string[]} HOGWARTS_HOUSES - The four canonical Hogwarts houses */
const HOGWARTS_HOUSES = ['Gryffindor', 'Hufflepuff', 'Ravenclaw', 'Slytherin'];

/**
 * SortingHat Component - Manages the house assignment ceremony
 * 
 * Provides an interactive sorting experience that assigns students to houses
 * using a balanced algorithm to ensure fair distribution across all houses.
 * 
 * @component
 * @returns {JSX.Element} The sorting hat interface with ceremony states
 */
export default function SortingHat() {
  /** @type {Function} navigationFunction - React Router navigation function */
  const navigate = useNavigate();
  
  /** @type {[string|null, Function]} assignedHouse - The house assigned to the current student */
  const [assignedHouse, setAssignedHouse] = useState(null);
  
  /** @type {[boolean, Function]} isPerformingCeremony - Whether the sorting ceremony is in progress */
  const [isPerformingCeremony, setIsPerformingCeremony] = useState(false);

  /**
   * Performs the house assignment ceremony using balanced distribution algorithm
   * 
   * Algorithm ensures fair house distribution by:
   * 1. Fetching current house populations from the school roster
   * 2. Identifying houses that maintain balance (population difference ≤ 1)
   * 3. Randomly selecting from valid candidates
   * 4. Persisting assignment to localStorage and backend database
   * 
   * @async
   * @function performHouseAssignment
   * @returns {Promise<void>}
   */
  const performHouseAssignment = async () => {
    const currentStudent = getCurrentUser();
    if (!currentStudent) return navigate('/');
    
    // Prevent duplicate sorting - redirect if already assigned
    if (currentStudent.house) {
      alert('You have already been sorted into a house!');
      return navigate('/home');
    }

    setIsPerformingCeremony(true);

    try {
      // Fetch current student roster to analyze house populations
      const studentsResponse = await fetch(`/api/students/${currentStudent.code}`);
      if (!studentsResponse.ok) throw new Error('Failed to fetch students');
      
      const studentRoster = await studentsResponse.json();
      if (!Array.isArray(studentRoster)) throw new Error('Invalid students data');
      
      // Calculate current house populations for balanced assignment
      const housePopulations = HOGWARTS_HOUSES.reduce((populationMap, houseName) => {
        populationMap[houseName] = studentRoster.filter(student => student.house === houseName).length;
        return populationMap;
      }, {});

      // Identify candidate houses that maintain population balance
      // Balance rule: after assignment, max population - min population ≤ 1
      const balancedHouseCandidates = [];
      for (const houseName of HOGWARTS_HOUSES) {
        const simulatedPopulations = { ...housePopulations };
        simulatedPopulations[houseName] = (simulatedPopulations[houseName] || 0) + 1;
        const populationValues = Object.values(simulatedPopulations);
        const maxPopulation = Math.max(...populationValues);
        const minPopulation = Math.min(...populationValues);
        if (maxPopulation - minPopulation <= 1) balancedHouseCandidates.push(houseName);
      }
      // Select final house assignment using balanced algorithm
      let selectedHouse;
      if (balancedHouseCandidates.length > 0) {
        // Random selection from houses that maintain balance
        selectedHouse = balancedHouseCandidates[Math.floor(Math.random() * balancedHouseCandidates.length)];
      } else {
        // Fallback: select randomly from least populated houses (edge case)
        const minimumPopulation = Math.min(...Object.values(housePopulations));
        const leastPopulatedHouses = HOGWARTS_HOUSES.filter(house => housePopulations[house] === minimumPopulation);
        selectedHouse = leastPopulatedHouses[Math.floor(Math.random() * leastPopulatedHouses.length)];
      }
      
      // Create updated student object with house assignment
      const updatedStudent = { ...currentStudent, house: selectedHouse, schoolId: currentStudent.code };
      
      // Persist assignment to localStorage for immediate app state update
      localStorage.setItem('hp_user', JSON.stringify(updatedStudent));
      
      // Prepare student data payload for backend persistence
      const studentDataPayload = {
        id: updatedStudent.id,
        name: updatedStudent.name,
        house: updatedStudent.house,
        points: updatedStudent.points || 0,
        bonusPoints: updatedStudent.bonusPoints || 0,
        schoolId: updatedStudent.schoolId,
      };
      
      // Persist house assignment to backend database
      const updateResponse = await fetch(`/api/students/${updatedStudent.schoolId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ students: [studentDataPayload] }),
      });
      
      if (!updateResponse.ok) throw new Error('Failed to update student house');
      
      //Wait one second
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Complete ceremony and reveal assigned house
      setIsPerformingCeremony(false);
      setAssignedHouse(selectedHouse);
    } catch (error) {
      console.error('House assignment error:', error);
      alert('Failed to assign house. Please try again.');
      setIsPerformingCeremony(false);
    }
  };

  // Render house assignment results after successful sorting
  if (assignedHouse) {
    return (
      <div style={styles.resultsContainer}>
        <h2 style={styles.announcementTitle}>
          The Sorting Hat has decided!
        </h2>
        <div style={styles.houseRevealSection}>
          <img 
            src={`/images/houses/${assignedHouse.toLowerCase()}.png`} 
            alt={`${assignedHouse} house crest`}
            style={styles.houseCrestImage}
          />
          <h1 style={styles.houseNameTitle}>
            {assignedHouse}
          </h1>
          <p style={styles.welcomeMessage}>
            Welcome to {assignedHouse}! You have been sorted into one of Hogwarts' most distinguished houses.
          </p>
        </div>
        <button onClick={() => navigate('/home')} style={styles.primaryButton}>
          Continue to Home
        </button>
      </div>
    );
  }

  // Render ceremony in progress with loading animation
  if (isPerformingCeremony) {
    return (
      <div style={styles.ceremonyContainer}>
        <h2 style={styles.ceremonyTitle}>The Sorting Hat is deciding...</h2>
        <img src="/images/sorting_hat.png" alt="Sorting Hat" style={styles.ceremonySortingHatImage} />
        <p style={styles.ceremonyMessage}>Hmm... let me see...</p>
      </div>
    );
  }

  // Render initial ceremony interface
  return (
    <div style={styles.initialContainer}>
      <h2 style={styles.initialTitle}>The Sorting Hat</h2>
      <img 
        src="/images/sorting_hat.png" 
        alt="The Sorting Hat - Place upon your head to be sorted" 
        style={styles.sortingHatImage} 
      />
      <p style={styles.initialInstructions}>Place the hat upon your head and discover your house.</p>
      <button onClick={performHouseAssignment} style={styles.primaryButton}>
        Reveal my house
      </button>
    </div>
  );
}

/**
 * Consolidated style definitions for the SortingHat component
 * Organizes all visual styling in one location for maintainability
 */
const styles = {
  // Results display after house assignment
  resultsContainer: {
    display: 'grid',
    gap: 24,
    justifyItems: 'center',
    textAlign: 'center',
  },
  announcementTitle: {
    color: '#eab308',
    fontSize: '2rem',
    margin: 0,
  },
  houseRevealSection: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
  },
  houseCrestImage: {
    width: 120,
    height: 120,
    objectFit: 'contain',
  },
  houseNameTitle: {
    fontSize: '3rem',
    margin: 0,
    fontFamily: "'Cinzel Decorative', serif",
    color: '#e2e8f0',
  },
  welcomeMessage: {
    fontSize: '1.2rem',
    opacity: 0.8,
    maxWidth: 400,
  },

  // Ceremony in progress display
  ceremonyContainer: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
    textAlign: 'center',
  },
  ceremonyTitle: {
    color: '#eab308',
  },
  ceremonySortingHatEmoji: {
    fontSize: '2rem',
    animation: 'pulse 1.5s ease-in-out infinite',
  },

  ceremonySortingHatImage: {
    width: 160,
    height: 'auto',
    objectFit: 'contain',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  ceremonyMessage: {
    opacity: 0.7,
  },

  // Initial ceremony interface
  initialContainer: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
  },
  initialTitle: {
    color: '#e2e8f0',
  },
  sortingHatImage: {
    width: 160,
    height: 'auto',
    objectFit: 'contain',
  },
  initialInstructions: {
    color: '#e2e8f0',
    opacity: 0.8,
  },

  // Shared button styling
  primaryButton: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#eab308',
    color: '#0b0f1a',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '1rem',
  },
};
